import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './MasterApplicants.css';
import {
  Search, Download, FileText, Linkedin, Loader2, X, Users, Building2, Globe,
  Briefcase, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, AlertCircle,
  Filter, UserPlus
} from 'lucide-react';
import TrackingModal from './TrackingModal';

const ITEMS_PER_PAGE = 20;

const getDisplayStatus = (tracking) => {
  if (!tracking || Object.keys(tracking).length === 0) return { text: 'Applied', className: 'mi-status-pending' };
  if (tracking.finalStatus === 'Rejected') return { text: 'Rejected', className: 'mi-status-rejected' };
  if (tracking.finalStatus === 'Shortlisted') return { text: 'Hired', className: 'mi-status-hired' };
  if (tracking.hrRoundStatus === 'Rejected' || tracking.round2Status === 'Rejected' || tracking.round1Status === 'Rejected' || tracking.onlineAssessmentStatus === 'Rejected') return { text: 'Rejected', className: 'mi-status-rejected' };
  return { text: 'In Process', className: 'mi-status-process' };
};

const getFileUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${process.env.REACT_APP_API_BASE_URL}/${path}`;
};

// 🚨 THE FIX: Now accepts `isCustomer` to hide internal recruiter names
const getSourceBadge = (source, isCustomer) => {
  if (!source) return { text: '—', className: 'mi-source-sourced', icon: null };
  if (source === 'College') return { text: 'College', className: 'mi-source-college', icon: <Building2 size={10} /> };
  if (source === 'Careers Page') return { text: 'Careers', className: 'mi-source-careers', icon: <Globe size={10} /> };
  
  if (source.startsWith('Sourced')) {
    // If it is a Customer, just show "Sourced Candidate". Otherwise, show "By RecruiterName"
    const textToDisplay = isCustomer ? 'Sourced Candidate' : source.replace('Sourced: ', 'By ');
    return { text: textToDisplay, className: 'mi-source-sourced', icon: <UserPlus size={10} /> };
  }
  
  return { text: source, className: 'mi-source-sourced', icon: null };
};

const MasterApplicants = ({ providedId, posterRole }) => {
  const [allApplicants, setAllApplicants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sourceFilter, setSourceFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('desc');

  const [currentPage, setCurrentPage] = useState(1);
  const [isTrackingModalOpen, setIsTrackingModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // 🚨 UI LOGIC: Detect if the current user is a Customer
  const isCustomerLogin = !!localStorage.getItem('customerId') || String(posterRole || '').toLowerCase().trim() === 'customer';

  useEffect(() => {
    const fetchMasterList = async () => {
      setIsLoading(true);
      try {
        const pId = providedId || localStorage.getItem('userId') || localStorage.getItem('recruiterId') || localStorage.getItem('customerId');
        const pRole = posterRole || (localStorage.getItem('customerId') ? 'Customer' : 'Recruiter');
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/job-postings/master-applicants`, {
          params: { posterId: pId, posterRole: pRole }
        });
        setAllApplicants(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        setError('Failed to load master applicant list.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchMasterList();
  }, [providedId, posterRole]);

  useEffect(() => { setCurrentPage(1); }, [searchQuery, sourceFilter, statusFilter, sortField, sortDir]);

  const sourceCounts = useMemo(() => {
    let all = allApplicants.length, college = 0, careers = 0, sourced = 0;
    allApplicants.forEach(a => {
      if (a.source === 'College') college++;
      else if (a.source === 'Careers Page') careers++;
      else if (a.source?.startsWith('Sourced')) sourced++;
    });
    return { all, college, careers, sourced };
  }, [allApplicants]);

  const processedApplicants = useMemo(() => {
    let list = allApplicants;

    if (sourceFilter === 'College') list = list.filter(a => a.source === 'College');
    else if (sourceFilter === 'Careers Page') list = list.filter(a => a.source === 'Careers Page');
    else if (sourceFilter === 'Sourced') list = list.filter(a => a.source?.startsWith('Sourced'));

    if (statusFilter !== 'All') {
      list = list.filter(a => {
        const st = getDisplayStatus(a.tracking).text;
        if (statusFilter === 'Applied') return st === 'Applied';
        if (statusFilter === 'In Process') return st === 'In Process';
        if (statusFilter === 'Hired') return st === 'Hired';
        if (statusFilter === 'Rejected') return st === 'Rejected';
        return true;
      });
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(app =>
        (app.fullName || '').toLowerCase().includes(q) ||
        (app.jobRole || '').toLowerCase().includes(q) ||
        (app.companyName || '').toLowerCase().includes(q) ||
        (app.collegeName || '').toLowerCase().includes(q) ||
        (app.email || '').toLowerCase().includes(q)
      );
    }

    if (sortField) {
      list = [...list].sort((a, b) => {
        if (sortField === 'name') return sortDir === 'asc' ? (a.fullName || '').localeCompare(b.fullName || '') : (b.fullName || '').localeCompare(a.fullName || '');
        if (sortField === 'job') return sortDir === 'asc' ? (a.jobRole || '').localeCompare(b.jobRole || '') : (b.jobRole || '').localeCompare(a.jobRole || '');
        if (sortField === 'cgpa') {
          const aV = parseFloat(a.currentCGPA) || 0;
          const bV = parseFloat(b.currentCGPA) || 0;
          return sortDir === 'asc' ? aV - bV : bV - aV;
        }
        return 0;
      });
    }

    return list;
  }, [allApplicants, sourceFilter, statusFilter, searchQuery, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(processedApplicants.length / ITEMS_PER_PAGE));
  const paginatedApplicants = processedApplicants.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const getPageNums = () => {
    const pages = [];
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + 4);
    if (end - start < 4) start = Math.max(1, end - 4);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }) => {
    const active = sortField === field;
    return (
      <span className="mi-sort-icons">
        <ChevronUp size={10} className={active && sortDir === 'asc' ? 'mi-si-on' : ''} />
        <ChevronDown size={10} className={active && sortDir === 'desc' ? 'mi-si-on' : ''} />
      </span>
    );
  };

  const handleExportExcel = () => {
    if (processedApplicants.length === 0) return alert('No applicants to export!');
    const exportData = processedApplicants.map((s, idx) => ({
      '#': idx + 1,
      'Job Applied': s.jobRole, 'Company': s.companyName, 
      'Source': isCustomerLogin && s.source?.startsWith('Sourced') ? 'Sourced Candidate' : s.source, // Hide in export too!
      'Status': getDisplayStatus(s.tracking).text, 'Full Name': s.fullName || '',
      'College': s.collegeName || 'N/A', 'Email': s.email || '', 'Phone': s.phone || '',
      'CGPA': s.currentCGPA || '', 'Experience': s.totalExperience || '0', 'LinkedIn': s.linkedinURL || ''
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'All Applicants');
    XLSX.writeFile(workbook, `Master_Applicants_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleRowClick = (student) => {
    setSelectedStudent(student);
    setIsTrackingModalOpen(true);
  };

  const handleCloseTrackingModal = (updatedData) => {
    setIsTrackingModalOpen(false);
    setSelectedStudent(null);
    if (updatedData) {
      const targetId = updatedData.id || updatedData._id || updatedData.application_id;
      setAllApplicants(prev => prev.map(app => String(app.application_id) === String(targetId) ? { ...app, tracking: updatedData.tracking } : app));
    }
  };

  if (isLoading) return (
    <div className="mi-root"><div className="mi-loading"><div className="mi-loading-card"><Loader2 className="mi-spin" size={36} /><span>Loading master inbox…</span></div></div></div>
  );

  if (error) return (
    <div className="mi-root"><div className="mi-loading"><div className="mi-loading-card mi-error-card"><AlertCircle size={40} className="mi-error-icon" /><span>{error}</span></div></div></div>
  );

  return (
    <div className="mi-root">
      <div className="mi-bg-blob mi-blob-1" />
      <div className="mi-bg-blob mi-blob-2" />

      {/* Header */}
      <div className="mi-header">
        <div className="mi-header-left">
          <div className="mi-title-icon"><Briefcase size={22} /></div>
          <div>
            <h1 className="mi-title">Master Inbox</h1>
            <p className="mi-subtitle">Consolidated view of all applicants across your jobs</p>
          </div>
        </div>
        <span className="mi-count-badge"><Users size={14} /> {allApplicants.length} total</span>
      </div>

      {/* Source Tabs */}
      <div className="mi-source-tabs">
        <button className={`mi-source-tab ${sourceFilter === 'All' ? 'active' : ''}`} onClick={() => setSourceFilter('All')}>
          <Users size={15} /> All Applicants <span className="mi-tab-count">{sourceCounts.all}</span>
        </button>
        <button className={`mi-source-tab ${sourceFilter === 'College' ? 'active' : ''}`} onClick={() => setSourceFilter('College')}>
          <Building2 size={15} /> College <span className="mi-tab-count">{sourceCounts.college}</span>
        </button>
        <button className={`mi-source-tab ${sourceFilter === 'Careers Page' ? 'active' : ''}`} onClick={() => setSourceFilter('Careers Page')}>
          <Globe size={15} /> Careers Page <span className="mi-tab-count">{sourceCounts.careers}</span>
        </button>
        <button className={`mi-source-tab ${sourceFilter === 'Sourced' ? 'active' : ''}`} onClick={() => setSourceFilter('Sourced')}>
          <UserPlus size={15} /> Sourced <span className="mi-tab-count">{sourceCounts.sourced}</span>
        </button>
      </div>

      {/* Toolbar */}
      <div className="mi-toolbar">
        <div className="mi-search-wrap">
          <Search size={16} className="mi-search-icon" />
          <input className="mi-search" type="text" placeholder="Search by name, job, college, email..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          {searchQuery && <button className="mi-clear-btn" onClick={() => setSearchQuery('')}><X size={12} /></button>}
        </div>

        <div className="mi-filter-group">
          <span className="mi-filter-label"><Filter size={11} /> Status</span>
          {['All', 'Applied', 'In Process', 'Hired', 'Rejected'].map(s => (
            <button
              key={s}
              className={`mi-filter-btn ${statusFilter === s ? `active mi-fb-${s.toLowerCase().replace(' ', '')}` : ''}`}
              onClick={() => setStatusFilter(s)}
            >{s}</button>
          ))}
        </div>

        <button className="mi-export-btn" onClick={handleExportExcel} disabled={processedApplicants.length === 0}>
          <Download size={14} /> Export Excel
        </button>
      </div>

      {/* Table */}
      <div className="mi-table-card">
        <div className="mi-table-wrap">
          <table className="mi-table">
            <thead>
              <tr>
                <th className="mi-th-status">Status</th>
                <th className="mi-th-name mi-sticky-col mi-sortable" onClick={() => handleSort('name')}>
                  Applicant Name <SortIcon field="name" />
                </th>
                <th className="mi-sortable" onClick={() => handleSort('job')}>
                  Job Applied <SortIcon field="job" />
                </th>
                <th>Company</th>
                <th>Source</th>
                <th>College / Origin</th>
                <th className="mi-sortable" onClick={() => handleSort('cgpa')}>
                  CGPA <SortIcon field="cgpa" />
                </th>
                <th>Email</th>
                <th>Links</th>
              </tr>
            </thead>
            <tbody>
              {paginatedApplicants.length === 0 ? (
                <tr>
                  <td colSpan="9" className="mi-empty-row">
                    <Users size={32} className="mi-empty-icon" />
                    <p>No applicants found in this view.</p>
                  </td>
                </tr>
              ) : (
                paginatedApplicants.map((app) => {
                  const statusInfo = getDisplayStatus(app.tracking);
                  
                  // 🚨 THE FIX: Call getSourceBadge with the isCustomerLogin check!
                  const srcBadge = getSourceBadge(app.source, isCustomerLogin);
                  
                  return (
                    <tr key={app.application_id} onClick={() => handleRowClick(app)} className="mi-row">
                      <td><span className={`mi-status-badge ${statusInfo.className}`}>{statusInfo.text}</span></td>
                      <td className="mi-td-name mi-sticky-col">
                        <div className="mi-name-wrap">
                          <div className="mi-name-avatar">{(app.fullName || '?')[0].toUpperCase()}</div>
                          <span>{app.fullName}</span>
                        </div>
                      </td>
                      <td className="mi-td-job">{app.jobRole || '—'}</td>
                      <td className="mi-td-secondary">{app.companyName || '—'}</td>
                      <td>
                        <span className={`mi-source-badge ${srcBadge.className}`}>
                          {srcBadge.icon} {srcBadge.text}
                        </span>
                      </td>
                      <td className="mi-td-secondary">{app.collegeName || <span className="mi-td-dim">—</span>}</td>
                      <td>{app.currentCGPA && app.currentCGPA !== 'Not Provided' ? <span className="mi-num-badge mi-num-blue">{app.currentCGPA}</span> : <span className="mi-td-dim">—</span>}</td>
                      <td className="mi-td-secondary">{app.email || '—'}</td>
                      <td className="mi-td-links">
                        {app.resume?.path && (
                          <a href={getFileUrl(app.resume.path)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="mi-icon-link mi-icon-pdf" title="Resume"><FileText size={14} /></a>
                        )}
                        {app.linkedinURL && app.linkedinURL !== 'Not Provided' && (
                          <a href={app.linkedinURL} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="mi-icon-link mi-icon-li" title="LinkedIn"><Linkedin size={14} /></a>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="mi-pagination">
            <span className="mi-page-info">
              Showing <strong>{(currentPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, processedApplicants.length)}</strong> of <strong>{processedApplicants.length}</strong>
            </span>
            <div className="mi-page-controls">
              <button className="mi-page-btn nav" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}><ChevronLeft size={14} /></button>
              {getPageNums().map(p => (
                <button key={p} className={`mi-page-btn ${currentPage === p ? 'active' : ''}`} onClick={() => setCurrentPage(p)}>{p}</button>
              ))}
              <button className="mi-page-btn nav" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}><ChevronRight size={14} /></button>
            </div>
          </div>
        )}
      </div>

      {selectedStudent && (
        <TrackingModal isOpen={isTrackingModalOpen} onClose={handleCloseTrackingModal} student={selectedStudent} />
      )}
    </div>
  );
};

export default MasterApplicants;